import { termMarketsByChain } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { termLenderKey } from '../../public-data/term/convertPublic'
import { termMarketCallCount } from './userCallBuild'
import { rebuildTermData, usesTermLens } from './lens'

const WAD = 1_000_000_000_000_000_000n

function toBigInt(v: any): bigint {
  if (v === undefined || v === null || v === '0x') return 0n
  if (typeof v === 'bigint') return v
  try {
    return BigInt(v as any)
  } catch {
    return 0n
  }
}

/**
 * Returns a [converter, expectedCallCount] tuple for Term Finance user data.
 *
 * Layout mirrors `buildTermUserCall`: per configured repo, a `balanceOf` result,
 * a `redemptionValue` result, a `getBorrowerRepurchaseObligation` result, then
 * one `getCollateralBalance` result per collateral leg. Emits one `UserData` per
 * repo key `TERM_FINANCE_<id>` (multi-market), each with a single sub-account.
 *
 * Lender (lent) side = the redeemable face value of the held repo tokens
 * (`balanceOf * redemptionValue / 1e18`, in purchaseToken units). Borrow side =
 * the outstanding repurchase obligation. Both are reported at face value; health
 * / borrow-capacity are computed downstream in `createMultiAccountTypeUserState`.
 */
export const getTermUserDataConverter = (
  _lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const markets = termMarketsByChain(chainId)
  const totalCalls = markets.reduce(
    (sum, m) => sum + termMarketCallCount(m.collateralParams.length),
    0,
  )

  const viaLens = usesTermLens(chainId)
  return [
    (raw: any[]) => {
      if (!raw) return undefined
      if (viaLens && raw.length !== 1) return undefined

      // One blob in, the walk's own flat array out — the cursor arithmetic below
      // is identical on both paths, so they cannot drift in interpretation.
      const data = viaLens ? rebuildTermData(raw[0], markets as any) : raw
      if (!data || data.length < totalCalls) return undefined

      const out: { [lender: string]: UserData } = {}
      let cursor = 0

      for (const market of markets) {
        const balanceResult = data[cursor]
        const redemptionResult = data[cursor + 1]
        const obligationResult = data[cursor + 2]
        const collateralResults = market.collateralParams.map(
          (_c, i) => data[cursor + 3 + i],
        )
        cursor += termMarketCallCount(market.collateralParams.length)

        const lenderKey = termLenderKey(market.termRepoId)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) continue

        const loanAddr = market.purchaseToken.toLowerCase()
        const loanUid = createMarketUid(chainId, lenderKey, loanAddr)
        const loanMeta = metaMap[loanUid]
        const loanDisplayPrice = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOraclePrice = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanPriceHist = loanMeta?.price?.priceUsd24h ?? loanDisplayPrice

        // Borrow side: outstanding repurchase obligation (purchaseToken units).
        const debtUnits = toBigInt(obligationResult)
        const debtStr = parseRawAmount(debtUnits.toString(), market.loanDecimals)
        const debtNum = Number(debtStr)

        // Lend side: redeemable face value of held repo tokens =
        // balanceOf * redemptionValue / 1e18 (purchaseToken units). Falls back
        // to the config `redemptionValue`, then to raw balance, if the read fails.
        const repoBalance = toBigInt(balanceResult)
        const redemptionValue =
          toBigInt(redemptionResult) ||
          toBigInt(market.redemptionValue) ||
          WAD
        const lentUnits = (repoBalance * redemptionValue) / WAD
        const depositsStr = parseRawAmount(lentUnits.toString(), market.loanDecimals)
        const depositsNum = Number(depositsStr)

        const posData: Record<string, any> = {}
        let anyBalance = debtUnits > 0n || lentUnits > 0n
        let depositsUSD24h = depositsNum * loanPriceHist

        // Loan side: borrower debt AND/OR lender lent position on the same market.
        posData[loanUid] = {
          marketUid: loanUid,
          underlying: loanAddr,
          deposits: depositsStr,
          debt: debtStr,
          debtStable: '0',
          depositsUSD: depositsNum * loanDisplayPrice,
          debtUSD: debtNum * loanDisplayPrice,
          debtStableUSD: 0,
          depositsUSDOracle: depositsNum * loanOraclePrice,
          debtUSDOracle: debtNum * loanOraclePrice,
          debtStableUSDOracle: 0,
          stableBorrowRate: '0',
          collateralEnabled: false,
          claimableRewards: 0,
        }

        // Collateral legs
        market.collateralParams.forEach((c, i) => {
          const collAmt = toBigInt(collateralResults[i])
          if (collAmt === 0n) return
          anyBalance = true
          const collAddr = c.token.toLowerCase()
          const collUid = createMarketUid(chainId, lenderKey, collAddr)
          const collMeta = metaMap[collUid]
          const collDec = collMeta?.asset?.decimals ?? c.decimals
          const collDisplayPrice = collMeta ? getDisplayPrice(collMeta) : 0
          const collOraclePrice = collMeta ? getOraclePrice(collMeta) : 0
          const collPriceHist = collMeta?.price?.priceUsd24h ?? collDisplayPrice
          const collDepositsStr = parseRawAmount(collAmt.toString(), collDec)
          const collDepositsNum = Number(collDepositsStr)
          depositsUSD24h += collDepositsNum * collPriceHist

          posData[collUid] = {
            marketUid: collUid,
            underlying: collAddr,
            deposits: collDepositsStr,
            debt: '0',
            debtStable: '0',
            depositsUSD: collDepositsNum * collDisplayPrice,
            debtUSD: 0,
            debtStableUSD: 0,
            depositsUSDOracle: collDepositsNum * collOraclePrice,
            debtUSDOracle: 0,
            debtStableUSDOracle: 0,
            stableBorrowRate: '0',
            collateralEnabled: true,
            claimableRewards: 0,
          }
        })

        if (!anyBalance) continue

        const userData = createMultiAccountTypeUserState(
          {
            chainId,
            account,
            lendingPositions: { '0': posData },
            modes: { '0': 0 },
          } as any,
          metaMap,
          {
            '0': {
              totalDeposits24h: depositsUSD24h,
              totalDebt24h: debtNum * loanPriceHist,
            },
          },
        )

        if (userData && userData.data.length > 0) out[lenderKey] = userData
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    // The CALL count the framework hands us: one blob on the lens path, the whole
    // per-repo walk otherwise. `totalCalls` stays the SLOT count the cursor
    // arithmetic above is written against.
    viaLens ? 1 : totalCalls,
  ]
}
